import { useState, useCallback } from "react";
import { useCurrentUser } from "utils/hooks";
import { useSelector, useDispatch } from "react-redux";
import { fetchOrganizationsAction } from "store/actions/organizations";
import {
  OrganizationSettings,
  OrganizationSetting,
} from "types/organizationSettings";
import i18n from "i18next";
import pluralize from "pluralize";

export const useOrganizationSettings = () => {
  const { user } = useCurrentUser();
  const organizationId = user?.activeOrganizationId;
  const dispatch = useDispatch();
  // Get organizations from Redux
  const organizations = useSelector(
    (state: any) => state.organizations.organizations
  );
  // Find the active org's settings in Redux
  const orgSettingsRedux: OrganizationSettings | null =
    organizations?.find((org: any) => org.uuid === organizationId)
      ?.organizationSettings || null;
  const [settings, setSettings] = useState<OrganizationSettings | null>(
    orgSettingsRedux
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update settings
  const updateOrganizationSettings = useCallback(
    async (newSettings: OrganizationSetting[]) => {
      if (!organizationId) {
        setError("Organization ID is required for updating settings.");
        throw new Error("Organization ID is required for updating settings.");
      }
      if (!process.env.REACT_APP_API_URL) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(process.env.REACT_APP_API_URL, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            organizationId,
            permissionKey: "manage_organization_settings",
          },
          body: JSON.stringify({
            query: `
              mutation UpdateOrgSettings($organizationId: ID!, $settings: [OrganizationSettingInput!]!) {
                updateOrganizationSettings(organizationId: $organizationId, settings: $settings) {
                  uuid
                  organizationId
                  settings {
                    settingKey
                    settingValue
                    lastChangedBy
                  }
                  created_at
                  updated_at
                }
              }
            `,
            variables: {
              organizationId,
              settings: newSettings.map(({ settingKey, settingValue }) => ({
                settingKey,
                settingValue: String(settingValue), // always send as string
              })),
            },
          }),
        });
        const json = await res.json();
        if (json.errors) {
          setError(json.errors[0].message);
        } else {
          setSettings(json.data.updateOrganizationSettings);
          // Optionally, refetch all orgs to update Redux
          if (typeof dispatch === "function") {
            // Fetch all orgs and update Redux
            const orgsRes = await fetch(process.env.REACT_APP_API_URL, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                organizationId,
                permissionKey: "read_organization_information",
              },
              body: JSON.stringify({
                query: `query { getOrganizations { uuid name organizationSettings { uuid organizationId settings { settingKey settingValue lastChangedBy } created_at updated_at } } }`,
              }),
            });
            const orgsJson = await orgsRes.json();
            if (orgsJson.data?.getOrganizations) {
              dispatch(
                fetchOrganizationsAction(orgsJson.data.getOrganizations)
              );
            }
          }
        }
      } catch (err: any) {
        setError(err.message || "Unknown error");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [organizationId, dispatch]
  );

  // Refetch latest settings from backend and update Redux
  const refetch = useCallback(async () => {
    if (!organizationId) {
      setError("Organization ID is required for refetching settings.");
      return;
    }
    if (!process.env.REACT_APP_API_URL) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(process.env.REACT_APP_API_URL, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          organizationId,
          permissionKey: "read_organization_information",
        },
        body: JSON.stringify({
          query: `query { getOrganizations { uuid name organizationSettings { uuid organizationId settings { settingKey settingValue lastChangedBy } created_at updated_at } } }`,
        }),
      });
      const json = await res.json();
      if (json.errors) {
        setError(json.errors[0].message);
      } else {
        // Update Redux
        if (typeof dispatch === "function") {
          dispatch(fetchOrganizationsAction(json.data.getOrganizations));
        }
        // Update local state
        const orgs = json.data.getOrganizations;
        const org = orgs.find((o: any) => o.uuid === organizationId);
        setSettings(org?.organizationSettings || null);
      }
    } catch (err: any) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [organizationId, dispatch]);

  return {
    settings,
    loading,
    error,
    updateOrganizationSettings,
    refetch,
  };
};

// Utility to get the preferred patient word for the current language and form
export function getPatientWord(
  orgSettings: OrganizationSettings | null,
  form: "singular" | "plural" = "singular"
) {
  let word = "patient";
  const lang = i18n.language || "en";

  const setting = orgSettings?.settings?.find(
    (s) => s.settingKey === "preferredPatientWord"
  );

  if (setting) {
    try {
      let val = setting.settingValue;

      if (typeof val !== "string") val = String(val);

      // Try to parse as JSON first (new format)
      try {
        const obj = JSON.parse(val);

        if (typeof obj === "string") {
          // If it's a string, try to parse it as JSON (handles double-encoded case)
          try {
            const innerObj = JSON.parse(obj);
            if (typeof innerObj === "object" && innerObj[lang]) {
              word = innerObj[lang];
            } else if (typeof innerObj === "object" && innerObj["en"]) {
              word = innerObj["en"];
            } else {
              word = obj; // fallback to the string itself
            }
          } catch (innerParseError) {
            // If inner parsing fails, use the string as-is
            word = obj;
          }
        } else if (typeof obj === "object" && obj[lang]) {
          const langValue = obj[lang];

          // If the language value is a string, try to parse it as JSON
          if (typeof langValue === "string") {
            try {
              const innerObj = JSON.parse(langValue);
              if (typeof innerObj === "object" && innerObj[lang]) {
                word = innerObj[lang];
              } else if (typeof innerObj === "object" && innerObj["en"]) {
                word = innerObj["en"];
              } else {
                word = langValue; // fallback to the string itself
              }
            } catch (innerParseError) {
              // If inner parsing fails, use the language value as-is
              word = langValue;
            }
          } else {
            word = langValue;
          }
        } else if (typeof obj === "object" && obj["en"]) {
          word = obj["en"];
        }
      } catch (parseError) {
        // If JSON parsing fails, treat as plain string (backward compatibility)
        word = val;
      }
    } catch (error) {
      // Handle any other errors silently
    }
  }

  if (form === "plural") {
    if (lang === "en") return pluralize(word);
    // fallback: add 'ים' for he, etc.
    if (lang === "he") return word + "ים";
    return word;
  }
  return word;
}

export function getPatientWordCapitalized(
  orgSettings: OrganizationSettings | null,
  form: "singular" | "plural" = "singular"
) {
  const word = getPatientWord(orgSettings, form);
  if (!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function getPatientWordPlural(orgSettings: OrganizationSettings | null) {
  return getPatientWord(orgSettings, "plural");
}

export function getPatientWordPluralCapitalized(
  orgSettings: OrganizationSettings | null
) {
  return getPatientWordCapitalized(orgSettings, "plural");
}

export default useOrganizationSettings;
